import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import apiClient from "../common/http.service";

// 🌟 NEW: Added search and category parameters to the hook
export const useProducts = (page = 1, limit = 12, search = "", category = "") => {
    const queryClient = useQueryClient();

    const productQuery = useQuery({
        // The QueryKey tells React to re-fetch the API whenever page, search, or category changes!
        queryKey: ["products", page, limit, search, category],
        queryFn: async () => {
            // Build the exact API URL with filters (e.g., /product?page=1&limit=12&search=bag)
            const params = new URLSearchParams({ page, limit });
            if (search) params.append("search", search);
            if (category) params.append("category", category);

            const response = await apiClient.get(`/product?${params.toString()}`);
            return response?.data || { data: [], count: 0, totalPages: 1, currentPage: 1 };
        },
        keepPreviousData: true
    });

    const createMutation = useMutation({
        mutationFn: (data) => apiClient.post("/product", data),
        onSuccess: () => {
            toast.success("Product created successfully");
            queryClient.invalidateQueries({ queryKey: ["products"] });
        },
        onError: (err) => toast.error(err.response?.data?.message || "Failed to create product"),
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => apiClient.put(`/product/${id}`, data),
        onSuccess: () => {
            toast.success("Product updated successfully");
            queryClient.invalidateQueries({ queryKey: ["products"] });
            queryClient.invalidateQueries({ queryKey: ["product"] }); // Invalidate single product too
        },
        onError: (err) => toast.error(err.response?.data?.message || "Failed to update product"),
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => apiClient.delete(`/product/${id}`),
        onSuccess: () => {
            toast.success("Product deleted successfully");
            queryClient.invalidateQueries({ queryKey: ["products"] });
        },
        onError: (err) => toast.error(err.response?.data?.message || "Failed to delete product"),
    });

    return {
        products: productQuery.data?.data || [],
        pagination: {
            totalPages: productQuery.data?.totalPages || 1,
            count: productQuery.data?.count || 0,
            currentPage: productQuery.data?.currentPage || page
        },
        isLoading: productQuery.isLoading,
        isError: productQuery.isError,
        createProduct: createMutation.mutateAsync,
        updateProduct: updateMutation.mutateAsync,
        deleteProduct: deleteMutation.mutateAsync,
        isCreating: createMutation.isPending,
        isUpdating: updateMutation.isPending,
        isDeleting: deleteMutation.isPending,
    };
};

export const useProduct = (id) => {
    return useQuery({
        queryKey: ["product", id],
        queryFn: async () => {
            const response = await apiClient.get(`/product/${id}`);
            return response?.data?.data;
        },
        enabled: !!id 
    });
};